/**
 * Step 5: Review / Edit generated content with live preview.
 * Heavy state-dependent — receives many props from ContentCreatePage.
 */
import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, Star, Hash, Languages } from "lucide-react";
import ContentPreview from "@/components/ContentPreview";
import TranslateDialog from "@/components/TranslateDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StepEdit({
  generated, seasonCtx,
  selectedContent, setSelectedContent,
  companyId, formatType, companies, formats,
  selectedMedia, media, thumbnails, variantThumbs, currentVariantKey,
}) {
  const [voiceScore, setVoiceScore] = useState(null);
  const [checkingVoice, setCheckingVoice] = useState(false);

  // Auto-check result from generation (incl. self-optimization)
  useEffect(() => {
    if (generated?._voice_check) setVoiceScore(generated._voice_check);
  }, [generated]);
  const [suggestingHashtags, setSuggestingHashtags] = useState(false);
  const [translateOpen, setTranslateOpen] = useState(false);
  const [fallbackImage, setFallbackImage] = useState(null);

  const fmt = formats.find((f) => f.value === formatType);

  const runHashtagSuggest = async () => {
    if (!selectedContent.caption) {
      toast.info("Waehle zuerst eine Caption");
      return;
    }
    setSuggestingHashtags(true);
    try {
      const res = await axios.post(
        `${API}/ai/hashtags`,
        { caption: selectedContent.caption, platform: fmt?.platform || "instagram", count: 12 },
        { withCredentials: true }
      );
      const newTags = res.data?.hashtags || [];
      const current = selectedContent.hashtags || generated?.hashtags || [];
      const merged = [...current];
      const seen = new Set(current.map((t) => t.toLowerCase()));
      for (const t of newTags) {
        if (!seen.has(t.toLowerCase())) {
          merged.push(t);
          seen.add(t.toLowerCase());
        }
      }
      setSelectedContent((p) => ({ ...p, hashtags: merged }));
      toast.success(`${newTags.length} Hashtags vorgeschlagen`);
    } catch (e) {
      toast.error("Hashtag-Vorschlag fehlgeschlagen");
    }
    setSuggestingHashtags(false);
  };

  const runVoiceCheck = async () => {
    setCheckingVoice(true);
    try {
      const res = await axios.post(
        `${API}/content/voice-check`,
        { company_id: companyId, text: selectedContent.caption },
        { withCredentials: true }
      );
      setVoiceScore(res.data);
    } catch (e) {
      console.error("Voice check failed:", e?.response?.status || e?.message);
    }
    setCheckingVoice(false);
  };

  const previewImage = (() => {
    if (!selectedMedia.length) return null;
    const first = media.find((m) => m.id === selectedMedia[0]);
    if (!first) return fallbackImage;
    return (currentVariantKey && variantThumbs[first.id]?.[currentVariantKey])
      || thumbnails[first.id]
      || fallbackImage;
  })();

  // Fallback: directly fetch the media by id when thumbnails arent ready or
  // the media object is not in the local `media` list yet (e.g. company switch).
  useEffect(() => {
    if (!selectedMedia.length) { setFallbackImage(null); return; }
    const firstId = selectedMedia[0];
    const first = media.find((m) => m.id === firstId);
    const haveThumb = first && (thumbnails[firstId] || (currentVariantKey && variantThumbs[firstId]?.[currentVariantKey]));
    if (haveThumb) { setFallbackImage(null); return; }
    let revokeUrl = null;
    let cancelled = false;
    (async () => {
      try {
        const m = first || (await axios.get(`${API}/media/${firstId}`, { withCredentials: true })).data;
        const path = (currentVariantKey && m.variants?.[currentVariantKey]?.storage_path) || m.storage_path;
        if (!path) return;
        const r = await axios.get(`${API}/media/file/${path}`, { withCredentials: true, responseType: "blob" });
        if (cancelled) return;
        const url = URL.createObjectURL(r.data);
        revokeUrl = url;
        setFallbackImage(url);
      } catch {
        if (!cancelled) setFallbackImage(null);
      }
    })();
    return () => { cancelled = true; if (revokeUrl) URL.revokeObjectURL(revokeUrl); };
  }, [selectedMedia, media, thumbnails, variantThumbs, currentVariantKey]);

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="grid grid-cols-[1fr_360px] gap-6">
        <div className="space-y-4">
          {seasonCtx && (
            <div className="bg-[#0F0F0F] text-white rounded-lg p-4 text-sm">
              <p className="font-medium">Saisonaler Kontext: {seasonCtx.season} - {seasonCtx.month_name}</p>
              <p className="text-white/60 text-xs mt-1">Themen: {seasonCtx.themes?.join(", ")}</p>
            </div>
          )}

          {/* Brand Voice Check */}
          <Card className="border-[var(--studio-border)] shadow-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Brand Voice Check</p>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  disabled={checkingVoice || !selectedContent.caption}
                  data-testid="voice-check-btn"
                  onClick={runVoiceCheck}
                >
                  {checkingVoice
                    ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    : <Star className="w-3 h-3 mr-1" />}
                  Pruefen
                </Button>
              </div>
              {voiceScore && (
                <div className="mt-3 p-3 rounded-lg bg-[var(--studio-bg)] animate-fade-in">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-2xl font-bold ${voiceScore.score >= 7 ? 'text-green-600' : voiceScore.score >= 4 ? 'text-amber-600' : 'text-red-600'}`}>
                      {voiceScore.score}/10
                    </span>
                    <Badge className={`text-xs border-0 ${voiceScore.brand_fit === 'gut' ? 'bg-green-100 text-green-800' : voiceScore.brand_fit === 'mittel' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>
                      {voiceScore.brand_fit}
                    </Badge>
                    {voiceScore.auto_optimized && (
                      <Badge className="text-xs border-0 bg-blue-100 text-blue-800" data-testid="voice-auto-optimized-badge">
                        ✨ automatisch optimiert{voiceScore.initial_score ? ` (vorher ${voiceScore.initial_score}/10)` : ""}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-[var(--studio-text-muted)]">{voiceScore.feedback}</p>
                  {voiceScore.improvements?.length > 0 && (
                    <ul className="mt-2 text-xs space-y-1">
                      {voiceScore.improvements.map((imp, i) => (
                        <li key={i} className="text-green-700">+ {imp}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Captions */}
          {generated.captions && (
            <Card className="border-[var(--studio-border)] shadow-none">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-base">Caption-Varianten</CardTitle>
                  {selectedContent.caption && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => setTranslateOpen(true)}
                      data-testid="open-translate-btn"
                    >
                      <Languages className="w-3 h-3 mr-1" /> Uebersetzen
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {generated.captions.map((c, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedContent((p) => ({ ...p, caption: c }))}
                    data-testid={`caption-variant-${i}`}
                    className={`w-full text-left p-3 rounded-lg border text-sm transition-all ${
                      selectedContent.caption === c
                        ? "border-[#2563EB] bg-[#2563EB]/5"
                        : "border-[var(--studio-border)]"
                    }`}
                  >
                    {c}
                  </button>
                ))}
                <Textarea
                  value={selectedContent.caption || ""}
                  onChange={(e) => setSelectedContent((p) => ({ ...p, caption: e.target.value }))}
                  className="mt-2"
                  rows={3}
                  placeholder="Oder eigenen Text schreiben..."
                />
              </CardContent>
            </Card>
          )}

          {/* Hooks */}
          {generated.hooks && (
            <Card className="border-[var(--studio-border)] shadow-none">
              <CardHeader><CardTitle className="font-heading text-base">Hook-Varianten</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {generated.hooks.map((h, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedContent((p) => ({ ...p, hook: h }))}
                    data-testid={`hook-variant-${i}`}
                    className={`w-full text-left p-3 rounded-lg border text-sm transition-all ${
                      selectedContent.hook === h
                        ? "border-[#2563EB] bg-[#2563EB]/5"
                        : "border-[var(--studio-border)]"
                    }`}
                  >
                    {h}
                  </button>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Reel Scenes */}
          {generated.scenes && (
            <Card className="border-[var(--studio-border)] shadow-none">
              <CardHeader><CardTitle className="font-heading text-base">Reel-Szenen</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {generated.scenes.map((s, i) => (
                  <div key={i} className="p-3 rounded-lg bg-[var(--studio-bg)] text-sm">
                    <p className="font-medium">Szene {s.number}: {s.description}</p>
                    {s.text_overlay && (
                      <p className="text-xs text-[var(--studio-text-muted)] mt-1">Einblendung: {s.text_overlay}</p>
                    )}
                    {s.duration && <Badge variant="outline" className="text-xs mt-1">{s.duration}</Badge>}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Hashtags */}
          <Card className="border-[var(--studio-border)] shadow-none">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-base">Hashtags</CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  disabled={suggestingHashtags}
                  onClick={runHashtagSuggest}
                  data-testid="suggest-hashtags-btn"
                >
                  {suggestingHashtags
                    ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    : <Hash className="w-3 h-3 mr-1" />}
                  KI-Vorschlag
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {(selectedContent.hashtags || generated.hashtags || []).length > 0 ? (
                <div className="flex flex-wrap gap-2" data-testid="hashtag-list">
                  {(selectedContent.hashtags || generated.hashtags || []).map((h, i) => (
                    <Badge key={i} variant="secondary">{h}</Badge>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-[var(--studio-text-muted)]">
                  Noch keine Hashtags. Klicke "KI-Vorschlag" fuer automatische Empfehlungen.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Schedule Suggestion */}
          {generated.schedule_suggestion && (
            <Card className="border-[var(--studio-border)] shadow-none">
              <CardHeader><CardTitle className="font-heading text-base">Posting-Vorschlag</CardTitle></CardHeader>
              <CardContent className="text-sm">
                <p>{generated.schedule_suggestion.day} um {generated.schedule_suggestion.time}</p>
                <p className="text-xs text-[var(--studio-text-muted)] mt-1">{generated.schedule_suggestion.reason}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Live Preview */}
        <div>
          <p className="text-sm font-medium mb-3">Vorschau</p>
          <ContentPreview
            platform={fmt?.platform || "instagram"}
            formatType={fmt?.type || "post"}
            caption={selectedContent.caption}
            hashtags={selectedContent.hashtags || generated?.hashtags}
            companyName={companies.find((c) => c.id === companyId)?.name}
            image={previewImage}
          />
        </div>
      </div>
      <TranslateDialog
        text={[selectedContent.caption, (selectedContent.hashtags || []).join(" ")].filter(Boolean).join("\n\n")}
        open={translateOpen}
        onOpenChange={setTranslateOpen}
      />
    </div>
  );
}
